import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import {
  SparklesIcon,
  XMarkIcon,
  ArrowPathIcon,
  CheckIcon,
  MusicalNoteIcon,
  ChevronDownIcon,
  InformationCircleIcon,
  CurrencyDollarIcon,
} from '@heroicons/react/24/outline';
import { musicApi } from '../../services/user/music.api';
import { InstrumentalityBalance } from '@packages/global';
import { useCoinStore } from '../../stores/coinStore';

const LYRICS_COST = 1;

interface Props {
  open: boolean;
  initialStyle?: string;
  onConfirm: (result: { caption: string; lyrics: string; suggestedBpm?: number; suggestedKey?: string }) => void;
  onClose: () => void;
}

export default function LyricsGeneratorModal({ open, initialStyle = '', onConfirm, onClose }: Props) {
  const { t } = useTranslation(['generation', 'common']);
  const { account } = useCoinStore();
  const balance = account?.balance ?? 0;
  const hasEnoughCoins = balance >= LYRICS_COST;

  const LANGUAGE_OPTIONS: { value: 'auto' | 'zh' | 'en'; label: string }[] = [
    { value: 'auto', label: t('generation:music.lyrics.langAuto') },
    { value: 'zh', label: t('generation:music.lyrics.langZh') },
    { value: 'en', label: t('generation:music.lyrics.langEn') },
  ];

  const BALANCE_OPTIONS: { value: InstrumentalityBalance | ''; label: string; desc: string }[] = [
    { value: 'vocals_only', label: t('generation:music.lyrics.balanceVocalsOnly'), desc: t('generation:music.lyrics.balanceVocalsOnlyDesc') },
    { value: 'vocal_focused', label: t('generation:music.lyrics.balanceVocalFocused'), desc: t('generation:music.lyrics.balanceVocalFocusedDesc') },
    { value: 'balanced', label: t('generation:music.lyrics.balanceBalanced'), desc: t('generation:music.lyrics.balanceBalancedDesc') },
    { value: 'very_instrumental', label: t('generation:music.lyrics.balanceVeryInstrumental'), desc: t('generation:music.lyrics.balanceVeryInstrumentalDesc') },
    { value: 'pure_instrumental', label: t('generation:music.lyrics.balancePureInstrumental'), desc: t('generation:music.lyrics.balancePureInstrumentalDesc') },
  ];

  const QUICK_TAGS: Record<string, { label: string; value: string }[]> = {
    [t('generation:music.lyrics.tagMood')]: [
      { label: t('generation:music.lyrics.moodMelancholic'), value: '忧郁(melancholic)' },
      { label: t('generation:music.lyrics.moodUplifting'), value: '振奋(uplifting)' },
      { label: t('generation:music.lyrics.moodEnergetic'), value: '充满活力(energetic)' },
      { label: t('generation:music.lyrics.moodDreamy'), value: '梦幻(dreamy)' },
      { label: t('generation:music.lyrics.moodDark'), value: '黑暗(dark)' },
      { label: t('generation:music.lyrics.moodNostalgic'), value: '怀旧(nostalgic)' },
      { label: t('generation:music.lyrics.moodEuphoric'), value: '欣快(euphoric)' },
      { label: t('generation:music.lyrics.moodAggressive'), value: '激进(aggressive)' },
    ],
    [t('generation:music.lyrics.tagInstrument')]: [
      { label: t('generation:music.lyrics.instAcousticGuitar'), value: '木吉他(acoustic guitar)' },
      { label: t('generation:music.lyrics.instPiano'), value: '钢琴(piano)' },
      { label: t('generation:music.lyrics.instSynth'), value: '合成器(synth pads)' },
      { label: t('generation:music.lyrics.inst808'), value: '808鼓(808 drums)' },
      { label: t('generation:music.lyrics.instStrings'), value: '弦乐(strings)' },
      { label: t('generation:music.lyrics.instElectricGuitar'), value: '电吉他(electric guitar)' },
      { label: t('generation:music.lyrics.instBass'), value: '贝斯(bass)' },
    ],
    [t('generation:music.lyrics.tagVocal')]: [
      { label: t('generation:music.lyrics.vocalFemale'), value: '女声(female vocal)' },
      { label: t('generation:music.lyrics.vocalMale'), value: '男声(male vocal)' },
      { label: t('generation:music.lyrics.vocalBreathy'), value: '气声(breathy)' },
      { label: t('generation:music.lyrics.vocalPowerful'), value: '有力(powerful)' },
      { label: t('generation:music.lyrics.vocalFalsetto'), value: '假声(falsetto)' },
      { label: t('generation:music.lyrics.vocalRaspy'), value: '沙哑(raspy)' },
      { label: t('generation:music.lyrics.vocalChoir'), value: '合唱(choir)' },
    ],
    [t('generation:music.lyrics.tagStyle')]: [
      { label: t('generation:music.lyrics.styleLofi'), value: 'Lo-Fi(lo-fi)' },
      { label: t('generation:music.lyrics.stylePop'), value: '流行(pop)' },
      { label: t('generation:music.lyrics.styleRock'), value: '摇滚(rock)' },
      { label: t('generation:music.lyrics.styleJazz'), value: '爵士(jazz)' },
      { label: t('generation:music.lyrics.styleRnb'), value: 'R&B(R&B)' },
      { label: t('generation:music.lyrics.styleHiphop'), value: '嘻哈(hip-hop)' },
      { label: t('generation:music.lyrics.styleElectronic'), value: '电子(electronic)' },
      { label: t('generation:music.lyrics.styleFolk'), value: '民谣(folk)' },
      { label: t('generation:music.lyrics.styleClassical'), value: '古典(classical)' },
    ],
  };

  const [style, setStyle] = useState(initialStyle);
  const [language, setLanguage] = useState<'zh' | 'en' | 'auto'>('auto');
  const [instrumentalBalance, setInstrumentalBalance] = useState<InstrumentalityBalance>('vocal_focused');
  const [extraGuidance, setExtraGuidance] = useState('');
  const [showQuickTags, setShowQuickTags] = useState(true);

  // 生成结果
  const [caption, setCaption] = useState('');
  const [lyrics, setLyrics] = useState('');
  const [suggestedBpm, setSuggestedBpm] = useState<number | undefined>();
  const [suggestedKey, setSuggestedKey] = useState<string | undefined>();

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [generated, setGenerated] = useState(false);
  const [activeTab, setActiveTab] = useState<'caption' | 'lyrics'>('lyrics');

  useEffect(() => {
    if (open) {
      setStyle(initialStyle);
      setCaption('');
      setLyrics('');
      setSuggestedBpm(undefined);
      setSuggestedKey(undefined);
      setError('');
      setGenerated(false);
      setActiveTab('lyrics');
    }
  }, [open, initialStyle]);

  if (!open) return null;

  const appendTag = (tag: string) => {
    setStyle(prev => {
      const trimmed = prev.trim();
      if (trimmed.includes(tag)) return prev;
      return trimmed ? `${trimmed}, ${tag}` : tag;
    });
  };

  const handleGenerate = async () => {
    if (!style.trim()) {
      setError(t('generation:music.lyrics.styleRequired'));
      return;
    }
    setError('');
    setLoading(true);
    try {
      const result = await musicApi.generateLyrics({
        style: style.trim(),
        language,
        instrumentalityBalance: instrumentalBalance,
        extraGuidance: extraGuidance.trim() || undefined,
      });
      setCaption(result.caption);
      setLyrics(result.lyrics);
      setSuggestedBpm(result.suggestedBpm);
      setSuggestedKey(result.suggestedKey);
      setGenerated(true);
      setActiveTab('lyrics');
    } catch (e: any) {
      setError(e.response?.data?.message || t('generation:music.lyrics.generateFailed'));
    } finally {
      setLoading(false);
    }
  };

  const handleConfirm = () => {
    if (!lyrics.trim()) return;
    onConfirm({ caption, lyrics, suggestedBpm, suggestedKey });
    onClose();
  };

  const isPureInstrumental = instrumentalBalance === 'pure_instrumental';

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      <div className="absolute inset-0 bg-black/60 backdrop-blur-sm" onClick={onClose} />

      <div className="relative w-full max-w-2xl bg-bg-form border border-border-subtle rounded-2xl shadow-2xl flex flex-col max-h-[92vh]">

        {/* 头部 */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-border-subtle flex-shrink-0">
          <div className="flex items-center gap-2">
            <MusicalNoteIcon className="w-5 h-5 text-primary" />
            <h2 className="text-base font-semibold text-gray-50">{t('generation:music.lyrics.modalTitle')}</h2>
            <span className="text-xs text-gray-500 bg-bg-elevated px-2 py-0.5 rounded-full">{t('generation:music.lyrics.modalSubtitle')}</span>
          </div>
          <button type="button" onClick={onClose}
            className="p-1.5 text-gray-400 hover:text-gray-200 hover:bg-bg-elevated rounded-lg transition-colors cursor-pointer">
            <XMarkIcon className="w-5 h-5" />
          </button>
        </div>

        {/* 内容区 */}
        <div className="flex-1 overflow-y-auto p-6 space-y-4">

          {/* 风格描述 */}
          <div>
            <div className="flex items-center justify-between mb-1.5">
              <label className="text-sm font-medium text-gray-300">
                {t('generation:music.lyrics.styleLabel')} <span className="text-red-400">*</span>
              </label>
              <button type="button" onClick={() => setShowQuickTags(v => !v)}
                className="flex items-center gap-1 text-xs text-primary hover:text-primary/80 transition-colors cursor-pointer">
                <SparklesIcon className="w-3 h-3" />
                {t('generation:music.lyrics.quickTags')}
                <ChevronDownIcon className={`w-3 h-3 transition-transform ${showQuickTags ? 'rotate-180' : ''}`} />
              </button>
            </div>
            <textarea
              value={style}
              onChange={(e) => setStyle(e.target.value)}
              rows={3}
              maxLength={500}
              placeholder={t('generation:music.lyrics.stylePlaceholder')}
              className="w-full bg-bg-surface border border-border-subtle rounded-xl px-4 py-3 text-sm text-gray-50 placeholder-gray-500 focus:outline-none focus:border-primary focus:ring-1 focus:ring-primary/30 resize-none transition-colors"
            />
            <div className="flex justify-between mt-1">
              {error && <p className="text-xs text-red-400">{error}</p>}
              <span className="text-xs text-gray-600 ml-auto">{style.length}/500</span>
            </div>

            {/* 快速标签面板 */}
            {showQuickTags && (
              <div className="mt-2 bg-bg-surface border border-border-subtle rounded-xl p-3 space-y-2">
                {Object.entries(QUICK_TAGS).map(([category, tags]) => (
                  <div key={category} className="flex items-start gap-2">
                    <span className="text-xs text-gray-500 w-8 flex-shrink-0 pt-0.5">{category}</span>
                    <div className="flex flex-wrap gap-1.5">
                      {tags.map(tag => (
                        <button key={tag.value} type="button" onClick={() => appendTag(tag.value)}
                          className={`text-xs px-2 py-0.5 rounded-md border transition-colors cursor-pointer ${style.includes(tag.value)
                            ? 'bg-primary/20 border-primary/40 text-primary'
                            : 'bg-bg-elevated border-border-subtle text-gray-400 hover:text-gray-200 hover:border-gray-500'
                            }`}>
                          {tag.label}
                        </button>
                      ))}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* 参数行 */}
          <div className="grid grid-cols-2 gap-4">
            {/* 语言 */}
            <div>
              <label className="block text-xs text-gray-400 mb-1.5">{t('generation:music.lyrics.lyricsLanguage')}</label>
              <div className="flex rounded-lg overflow-hidden border border-border-subtle">
                {LANGUAGE_OPTIONS.map((opt) => (
                  <button key={opt.value} type="button"
                    onClick={() => setLanguage(opt.value)}
                    className={`flex-1 py-2 text-xs font-medium transition-colors cursor-pointer ${language === opt.value
                      ? 'bg-primary text-white'
                      : 'bg-bg-elevated text-gray-400 hover:text-gray-200'
                      }`}>
                    {opt.label}
                  </button>
                ))}
              </div>
            </div>

            {/* 额外引导 */}
            <div>
              <label className="block text-xs text-gray-400 mb-1.5">{t('generation:music.lyrics.extraGuidance')}</label>
              <input type="text" value={extraGuidance}
                onChange={(e) => setExtraGuidance(e.target.value)}
                maxLength={200}
                placeholder={t('generation:music.lyrics.extraGuidancePlaceholder')}
                className="w-full bg-bg-elevated border border-border-subtle rounded-lg px-3 py-2 text-sm text-gray-50 placeholder-gray-500 focus:outline-none focus:border-primary transition-colors"
              />
            </div>
          </div>

          {/* 人声/器乐平衡 */}
          <div>
            <label className="block text-xs text-gray-400 mb-1.5">{t('generation:music.lyrics.balanceLabel')}</label>
            <div className="flex gap-2">
              {BALANCE_OPTIONS.map((opt) => (
                <button key={opt.value} type="button"
                  onClick={() => setInstrumentalBalance(opt.value as InstrumentalityBalance)}
                  title={opt.desc}
                  className={`flex-1 py-2 text-xs font-medium rounded-lg border transition-colors cursor-pointer ${instrumentalBalance === opt.value
                    ? 'bg-primary/15 border-primary/50 text-primary'
                    : 'bg-bg-elevated border-border-subtle text-gray-400 hover:text-gray-200'
                    }`}>
                  {opt.label}
                </button>
              ))}
            </div>
            <p className="text-xs text-gray-600 mt-1">
              {BALANCE_OPTIONS.find(o => o.value === instrumentalBalance)?.desc}
            </p>
          </div>

          {/* 生成按钮 */}
          <div className="space-y-2">
            <button type="button" onClick={handleGenerate}
              disabled={loading || !style.trim() || !hasEnoughCoins}
              className="w-full flex items-center justify-center gap-2 py-3 rounded-xl bg-primary/10 border border-primary/30 text-primary hover:bg-primary/20 disabled:opacity-40 disabled:cursor-not-allowed transition-colors font-medium text-sm cursor-pointer">
              {loading ? (
                <><ArrowPathIcon className="w-4 h-4 animate-spin" />{t('generation:music.lyrics.generating')}</>
              ) : (
                <><SparklesIcon className="w-4 h-4" />{generated ? t('generation:music.lyrics.regenerate') : t('generation:music.lyrics.aiGenerate')}</>
              )}
            </button>

            {/* 费用 & 余额 */}
            <div className="flex items-center justify-between px-1 text-xs">
              <div className="flex items-center gap-1 text-gray-500">
                <CurrencyDollarIcon className="w-3.5 h-3.5" />
                <span>{t('generation:music.lyrics.costPerGeneration')} <span className="text-primary font-mono">{LYRICS_COST}</span> {t('generation:music.lyrics.costUnit')}</span>
              </div>
              <div className="flex items-center gap-1">
                <span className="text-gray-500">{t('generation:music.form.balance')}</span>
                <span className={`font-mono ${hasEnoughCoins ? 'text-gray-300' : 'text-red-400'}`}>
                  {balance}
                </span>
                {!hasEnoughCoins && (
                  <span className="text-red-400 ml-1">{t('generation:music.lyrics.insufficientBalance')}</span>
                )}
              </div>
            </div>
          </div>

          {/* 生成结果区 */}
          {generated && !loading && (
            <div className="space-y-3">
              {/* 建议参数提示 */}
              {(suggestedBpm || suggestedKey) && (
                <div className="flex items-center gap-3 bg-primary/5 border border-primary/20 rounded-lg px-3 py-2">
                  <InformationCircleIcon className="w-4 h-4 text-primary flex-shrink-0" />
                  <span className="text-xs text-gray-300">
                    {t('generation:music.lyrics.aiSuggestion')}
                    {suggestedBpm && <span className="text-primary ml-1">{t('generation:music.lyrics.bpmSuggestion', { bpm: suggestedBpm })}</span>}
                    {suggestedKey && <span className="text-primary ml-2">{suggestedKey}</span>}
                    <span className="text-gray-500 ml-2">{t('generation:music.lyrics.syncHint')}</span>
                  </span>
                </div>
              )}

              {/* Tab 切换 */}
              <div className="flex rounded-lg overflow-hidden border border-border-subtle">
                <button type="button" onClick={() => setActiveTab('lyrics')}
                  className={`flex-1 py-2 text-xs font-medium transition-colors cursor-pointer ${activeTab === 'lyrics' ? 'bg-primary text-white' : 'bg-bg-elevated text-gray-400 hover:text-gray-200'
                    }`}>
                  {t('generation:music.lyrics.lyricsTab')} {isPureInstrumental ? t('generation:music.lyrics.lyricsTabInstrumental') : ''}
                </button>
                <button type="button" onClick={() => setActiveTab('caption')}
                  className={`flex-1 py-2 text-xs font-medium transition-colors cursor-pointer ${activeTab === 'caption' ? 'bg-primary text-white' : 'bg-bg-elevated text-gray-400 hover:text-gray-200'
                    }`}>
                  {t('generation:music.lyrics.captionTab')}
                </button>
              </div>

              {activeTab === 'lyrics' && (
                <div>
                  <div className="flex justify-between items-center mb-1">
                    <span className="text-xs text-gray-500">{t('generation:music.lyrics.editableHint')}</span>
                    <span className="text-xs text-gray-600">{lyrics.length}/5000</span>
                  </div>
                  <textarea value={lyrics} onChange={(e) => setLyrics(e.target.value)}
                    rows={14} maxLength={5000}
                    className="w-full bg-bg-surface border border-border-subtle rounded-xl px-4 py-3 text-sm text-gray-50 focus:outline-none focus:border-primary focus:ring-1 focus:ring-primary/30 resize-none font-mono transition-colors"
                  />
                </div>
              )}

              {activeTab === 'caption' && (
                <div>
                  <div className="flex justify-between items-center mb-1">
                    <span className="text-xs text-gray-500">{t('generation:music.lyrics.captionSyncHint')}</span>
                    <span className="text-xs text-gray-600">{caption.length}/500</span>
                  </div>
                  <textarea value={caption} onChange={(e) => setCaption(e.target.value)}
                    rows={5} maxLength={500}
                    className="w-full bg-bg-surface border border-border-subtle rounded-xl px-4 py-3 text-sm text-gray-50 focus:outline-none focus:border-primary focus:ring-1 focus:ring-primary/30 resize-none transition-colors"
                  />
                </div>
              )}
            </div>
          )}
        </div>

        {/* 底部操作 */}
        <div className="flex items-center justify-end gap-3 px-6 py-4 border-t border-border-subtle flex-shrink-0">
          <button type="button" onClick={onClose}
            className="px-4 py-2 text-sm text-gray-400 hover:text-gray-200 hover:bg-bg-elevated rounded-lg transition-colors cursor-pointer">
            {t('common:cancel')}
          </button>
          <button type="button" onClick={handleConfirm}
            disabled={!lyrics.trim() && !caption.trim()}
            className="flex items-center gap-2 px-5 py-2 bg-primary hover:bg-primary/90 disabled:opacity-40 disabled:cursor-not-allowed text-white text-sm font-medium rounded-lg transition-colors cursor-pointer">
            <CheckIcon className="w-4 h-4" />
            {t('generation:music.lyrics.useResult')}
          </button>
        </div>
      </div>
    </div>
  );
}
